import { useEffect, useState } from "react";
import { FileSearch, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtDate } from "@/lib/api";

const TONE = { add: "bg-emerald-100 text-emerald-800", change: "bg-amber-100 text-amber-800", delete: "bg-red-100 text-red-800", keep: "bg-slate-100 text-slate-600", review: "bg-violet-100 text-violet-800" };
const ACTIONS = ["add", "change", "delete", "keep", "review"];

const Row = ({ r }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`annex-check-row-${r.hs_code}`}>
    <td className="py-1.5 pr-2 font-mono">{r.hs_code}</td>
    <td className="py-1.5 pr-2"><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase ${TONE[r.action]}`}>{r.action}</span>{r.edited && <span className="ml-1 text-[11px] text-amber-700">edited</span>}</td>
    <td className="py-1.5 pr-2">{r.commodity} · {r.scope}<div className="text-slate-500">{r.description}</div>{r.fields.length > 0 && r.current && <div className="text-[11px] text-slate-400">changes: {r.fields.join(", ")}</div>}</td>
    <td className="py-1.5 pr-2 text-slate-600">{r.condition}</td>
    <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDate(r.applies_from)}</td>
    <td className="py-1.5 text-slate-500">{r.source}</td>
  </tr>
);

export const AnnexCheckReport = () => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [f, setF] = useState("all");
  useEffect(() => { if (open && !d) api.get("/annex/check-report").then((r) => setD(r.data)); }, [open, d]);
  const rows = d ? d.rows.filter((r) => f === "all" || r.action === f) : [];
  return (
    <>
      <Button variant="outline" data-testid="annex-check-report-btn" onClick={() => setOpen(true)}><FileSearch className="mr-1.5 h-4 w-4" />Annex I check report</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] max-w-6xl overflow-y-auto" data-testid="annex-check-report">
          <DialogHeader><DialogTitle>Annex I check report</DialogTitle></DialogHeader>
          {!d ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
            <div className="space-y-3 text-xs">
              <p className="text-sm text-slate-600" data-testid="annex-check-against">Checked against {d.checked_against} on {fmtDate(d.checked_on)}. Changes are offered as update v{d.update_version} through Review &amp; apply.</p>
              <ul className="list-disc pl-5 text-slate-600">{d.notes.map((n) => <li key={n}>{n}</li>)}</ul>
              <div className="flex flex-wrap gap-2">
                {["all", ...ACTIONS].map((a) => (
                  <button key={a} data-testid={`annex-check-filter-${a}`} onClick={() => setF(a)} className={`rounded-full border px-3 py-1 ${f === a ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white"}`}>
                    {a} {a === "all" ? d.rows.length : d.summary[a]}
                  </button>
                ))}
              </div>
              <table className="w-full">
                <thead><tr className="text-left uppercase tracking-wider text-slate-400"><th>Code</th><th>Action</th><th>Entry</th><th>Condition</th><th>Applies from</th><th>Legal source</th></tr></thead>
                <tbody>{rows.map((r) => <Row key={r.hs_code} r={r} />)}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AnnexCheckedAgainst = () => {
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/annex/version").then((r) => setV(r.data)); }, []);
  if (!v) return null;
  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600" data-testid="annex-checked-against">
      {v.checked_against ? <>Checked against: <b>{v.checked_against}</b> on <b>{fmtDate(v.checked_on)}</b> · update version <b data-testid="annex-installed-version">v{v.version}</b></>
        : <>Annex I table version <b data-testid="annex-installed-version">v{v.version}</b> · not yet checked against the consolidated text (apply the available update)</>}
      <ul className="mt-2 list-disc pl-5 text-xs text-slate-500">{v.notes.map((n) => <li key={n}>{n}</li>)}</ul>
    </div>
  );
};
